import React, {
    useEffect,
    useContext,
    useState,
} from "react"

import {
    Badge,
    Button,
    Container,
    Form,
    Col,
    Row,
} from "react-bootstrap"

import {
    useAuthenticator,
} from "@aws-amplify/ui-react"

import {
    useLocation,
    useNavigate,
} from "react-router-dom"

import * as EMETL from "../API"
import EMETLSegmentedControl from "./Components/EMETLSegmentedControl"

import CreateAssessmentVM, {
    StyleModel
} from "../ViewModel/CreateAssessment.vm"

import {
    Context, EMETLContext,
} from "../Model/Contexts/AppContext"

import EMETLAssessmentManager, { 
    EMETLDetailedAssessment, 
    EMETLAssessmentTaskWithDetails,
    EMETLBatchUpdateAssessed,
    EMETLBatchUpdateAssessmentTask,
} from "../Model/EMETLAssessment.m"

import AssessmentTasks from "./AssessmentTasks"
import EMETLFieldManual from "./Components/EMETLFieldManual"
import EMETLPersonnel from "./Components/EMETLPersonnel"
import Personnel from "./Personnel"
import EMETLDateRangeHeader from "./Components/Header/EMETLDateRangeHeader"
import EMETLDateSelection from "./Components/EMETLDateSelection"

export type CreateAssessmentProps = {
    style? : StyleModel,
    editingAssessment?: EMETLDetailedAssessment,
    doneEditing?: (success: boolean) => void,
}

let selectedTasks: number[] = []
let selectedPersonnel: string[] = []
let needToAddTasks: EMETLAssessmentTaskWithDetails[] = []
let needToAddPersonnel: string[] = []
let needToRemoveAssessmentTaskIDs: number[] = []
let savedAssessedUsers: EMETL.Assessed[] = []
let needToRemovePersonnel: number[] = []
let persistedAssessment: EMETLDetailedAssessment | null = null
let persistedUnit: EMETL.Unit | null = null

export const clearSelected = () => {
    selectedTasks = []
    selectedPersonnel = []
    needToAddTasks = []
    needToAddPersonnel = []
    needToRemoveAssessmentTaskIDs = []
    savedAssessedUsers = []
    needToRemovePersonnel = []
}

const CreateAssessment = (props?: CreateAssessmentProps) => {
    CreateAssessmentVM.ctx = useContext(Context) as EMETLContext
    const {
        amplify,
        units: {
            value: u,
        },
        staticTasksAndPS: {
            value: t,
        },
        loading: {
            update: setLoading,
            setMessage: setLoadingMessage,
        },
        banner: {
            setInfo,
            setWarning,
            setError,
        },
        assessmentData: {
            assessmentTasks: {
                update: setAssessmentTasks,
                value: assessmentTasks,
            },
            assessments: {
                update: setAssessments,
            }
        },
        modal: {
            content,
            setContent,
            setTitle,
        },
        personnel: {
            value: dudes,
            update: setPersonnel,
        },
        header: {
            update: setHeader,
        }
    } = CreateAssessmentVM.ctx

    const {
        doneEditing = (_success: boolean) => {/* */},
        editingAssessment,
    } = props

    const {
        user,
    } = useAuthenticator()

    const location = useLocation()
    const navigate = useNavigate()
    
    const {
        assessment: assessmentFromParams,
    } = location?.state || {}

    const priorAssessment = editingAssessment ?? assessmentFromParams
    const [assessment, setAssessment] = useState<EMETLDetailedAssessment>(priorAssessment ?? {
        Title: "",
        TrainingType: 0,
    })
    const [unitForAssessment, setUnitForAssessment] = useState<EMETL.Unit | null>(null)
    const [startDate, setStartDate] = useState<string>(priorAssessment?.StartDate || "")
    const [endDate, setEndDate] = useState<string>(priorAssessment?.EndDate ||"")
    const [templates, setTemplates] = useState<EMETL.ListAssessmentResult[] | null>(null)
    const [_selectedUnitIndex, setSelectedUnitIndex] = useState<number>(0)

    const load = async () => {
        const templatesResp = await CreateAssessmentVM.loadTemplates()
        if (templatesResp && !!templatesResp?.length)
            setTemplates(templatesResp)

        if (!dudes?.length) {
            const p = await CreateAssessmentVM.PersonnelManager.loadPersonnel()

            if (p) {
                setPersonnel(CreateAssessmentVM.PersonnelManager.sortPersonnel(p))
            }
        }

        if (assessment?.ID && assessment?.BranchID) {
            if (!assessmentTasks?.length) {
                const resp = await CreateAssessmentVM.getAssessmentTasks(assessment.ID)
                if (resp) {
                    if (resp?.length)
                        setAssessmentTasks(resp)

                    selectedTasks = resp.map(at => at.TaskID)
                }
            }

            if (!selectedPersonnel?.length) {
                const resp = await CreateAssessmentVM.listAssessedUsersForAssessment(assessment.ID)
                if (resp) {
                    if (resp?.length) {
                        selectedPersonnel = resp.map(u => u.UserID)
                        savedAssessedUsers = resp
                    }
                }
            }

            return await CreateAssessmentVM.getUnitForBranch(assessment.BranchID)
        }

        return null
    }

    const _init = async () => {
        const res = await load()
        if (res && res?.ID && u) {
            setUnitForAssessment(res)
            setSelectedUnitIndex(u.findIndex(unit => unit?.ID === res.ID))
        }
        else if(u && !!u?.length) {
            setUnitForAssessment(null)
            setSelectedUnitIndex(-1)
        }
    }

    const refresh = async () => {
        await _init()
        // Set additional state variables here...
    }

    const createOrUpdateAssessment = async (
        atAdd: EMETLBatchUpdateAssessmentTask[], 
        atRemove: number[],
        pAdd: EMETLBatchUpdateAssessed[],
        pRemove: number[],
    ) => {
        if (!assessment?.Title) {
            setError("Please enter a title for the assessment.")
            return
        }
        setLoading(true)
        if (!unitForAssessment?.ID || !unitForAssessment?.Depth) {
            alert("Please select a unit for this assessment.")
            setLoading(false)
            
            return
        }
        let newAssessment = structuredClone(persistedAssessment ?? assessment)
        setLoadingMessage(`Loading branch ID for assessment at unit #${unitForAssessment.ID}`)
        const branch = await CreateAssessmentVM.getBranchForUnit(unitForAssessment.ID, unitForAssessment.Depth)
        if (!branch) {
            setError("Error fetching branch for unit.")
            setLoading(false)
            setLoadingMessage("")
            doneEditing(false)
            return
        }
        newAssessment = {
            ...newAssessment,
            BranchID: branch.ID,
        }
        const assessmentDetails = CreateAssessmentVM.detailedAssessmentToAssessmentDetail(newAssessment)
        const tasksToBeAdded = atAdd?.map((at: EMETLBatchUpdateAssessmentTask) => {
            return {
                ID: null,
                TaskID: at.TaskID,
                Rating: at.Rating,
                SpecificNeeds: at.SpecificNeeds,
            }
        })
        if (!newAssessment?.ID) {
            const isQuarterly = startDate && endDate
            let taskIDs = !!tasksToBeAdded?.length ? JSON.stringify(tasksToBeAdded) : null
            if (isQuarterly) {
                const allTasks = await EMETLAssessmentManager.loadTasks()
                if (allTasks && !!allTasks?.length) {
                    taskIDs = JSON.stringify(allTasks.map(t => {
                        return {
                            ID: null,
                            TaskID: t.ID,
                            Rating: 0,
                            SpecificNeeds: "",
                        }
                    }))
                }
            }
            const newAssessmentID = await CreateAssessmentVM.createNewAssessmentAndDetails(
                newAssessment,
                doneEditing,
                assessmentDetails,
                taskIDs,
                pAdd?.length ? JSON.stringify(pAdd) : null,
            )
            if (!newAssessmentID) {
                setLoading(false)
                setLoadingMessage("")
                setError("Error creating assessment. Please try again.")
                doneEditing(false)
                return
            }
            if (isQuarterly) {
                setLoadingMessage("Saving quarterly assessment details")
                await CreateAssessmentVM.createQuarterlyAssessmentDetail({
                    AssessmentID: newAssessmentID,
                    StartDate: startDate,
                    EndDate: endDate,
                })
            }
            const resp = await CreateAssessmentVM.loadAssessmentData()
            if (!!resp?.length)
                setAssessments(resp)
            
        } else {
            await CreateAssessmentVM.updateExistingAssessmentAndDetails(
                newAssessment,
                doneEditing,
                assessmentDetails,
                !!tasksToBeAdded?.length ? JSON.stringify(tasksToBeAdded) : null,
                !!atRemove?.length ? JSON.stringify(atRemove) : null,
                pAdd?.length ? JSON.stringify(pAdd) : null,
                !!pRemove?.length ? JSON.stringify(pRemove) : null,
            )
        }
        setAssessment(newAssessment)
        
        setLoading(false)
        setLoadingMessage("")
        navigate("/")
    }

    const handleTaskSelection = (s: number[]) => {
        setTitle("")
        setContent(null)
        selectedTasks = s
        const s1 = new Set(selectedTasks)
        const s2 = new Set(assessmentTasks?.map(at => at.TaskID))
        const diff = [...s1].filter(taskID => !s2.has(taskID))
        if (!!diff?.length) {
            const newTasks = EMETLAssessmentManager.defaultAssessmentTaskDataForIDs(diff)
            needToAddTasks = [
                ...needToAddTasks.filter(nt => s1.has(nt.TaskID)),
                ...newTasks.filter(nt => !needToAddTasks?.find(at => at.TaskID === nt.TaskID)),
            ]
        } else {
            needToAddTasks = []
        }
        const needToRemoveTaskIDs = [...s2].filter(taskID => !s1.has(taskID))
        needToRemoveAssessmentTaskIDs = assessmentTasks?.filter(at => needToRemoveTaskIDs.includes(at.TaskID)).map(at => at.ID)
    }

    const handlePersonnelSelection = (s: string[]) => {
        setTitle("")
        setContent(null)
        const s1 = new Set(s)
        const s2 = new Set(savedAssessedUsers.map(u => u.UserID))
        const diff = [...s1].filter(userID => !s2.has(userID))
        if (!!diff?.length) {
            const n = new Set(needToAddPersonnel)
            needToAddPersonnel = [
                ...needToAddPersonnel.filter(np => s1.has(np)),
                ...diff.filter(np => !n.has(np)),
            ]
        } else {
            needToAddPersonnel = []
        }
        const needToRemovePersonnelUserIDs = [...s2].filter(userID => !s1.has(userID))
        needToRemovePersonnel = savedAssessedUsers?.filter(u => needToRemovePersonnelUserIDs.includes(u.UserID)).map(u => u.ID)
        selectedPersonnel = s
    }

    const addOrRemoveTasks = () => {
        setContent(
            <EMETLFieldManual 
                wantsSelection={true} 
                doneSelecting={handleTaskSelection} 
                tasksAlreadySelected={selectedTasks}
                fromModal={true}
            />
        )
        setTitle("Select Tasks for Assessment")
    }

    const addOrRemovePersonnel = () => {
        setContent(
            <Personnel 
                wantsSelection={true}
                doneSelecting={handlePersonnelSelection}
                dudesAlreadySelected={selectedPersonnel}
            />
        )
        setTitle("Select Personnel to Assess")
    }

    const deleteAssessment = async () => {
        if (!assessment?.ID)
            return

        const shouldDelete = confirm(
            `Are you sure you want to delete assessment ${
                assessment.ID
            } and all related data? This action cannot be undone.`
        )
        if (shouldDelete) {
            setLoading(true)
            setLoadingMessage(`Deleting assessment #${assessment.ID}`)
            const resp = await CreateAssessmentVM.deleteAssessment(assessment.ID)
            setLoading(false)
            setLoadingMessage("")
            if (resp) {
                setInfo(`Assessment ${assessment.ID} deleted successfully.`)
                doneEditing(true)
            } else {
                setError(`Error deleting assessment ${assessment.ID}. Please try again.`)
            }
            navigate("/")
        }
    }

    const handleDateSubmit = (startDate: string, endDate: string) => {
        if (!(startDate && endDate)) {
            setInfo("Quarterly assessments require both a start and end date.")
            return
        }
        const s = new Date(startDate)
        const e = new Date(endDate)
        if (s >= e) {
            setInfo("The start date must be before the end date.")
            return
        }
        setStartDate(startDate)
        setEndDate(endDate)
        selectedTasks = []
        setAssessment({
            ...assessment,
            Title: `Quarterly Assessment ${startDate} to ${endDate}`,
        })
        setInfo(`This assessment will be saved as quarterly from ${startDate} to ${endDate}.`)
    }

    const editDates = () => {
        setContent(
            <EMETLDateSelection 
                setStartDate={(s) => handleDateSubmit(s, endDate)}
                setEndDate={(e) => handleDateSubmit(startDate, e)}
                initialStartDate={new Date(startDate)}
                initialEndDate={new Date(endDate)}
            />
        )
        setTitle("Edit Quarterly Assessment Dates")
    }

    const loadTemplateData = async (templateID: string) => {
        const template = templates.find(t => t.ID.toString() === templateID)
        if (!template) {
            setError("Invalid template data.")
            
            return
        }

        const templateDetail = await CreateAssessmentVM.loadAssessment(template?.ID)
        if (!templateDetail) {
            setError("Error loading template data.")
            
            return
        }
        templateDetail.ID = null
        templateDetail.AssessmentDetailID = null
        templateDetail.QuarterlyAssessmentDetailID = null
        templateDetail.IsTemplate = 0
        setAssessment(templateDetail)
        handleTaskSelection(template?.TaskNumbers?.split(",")?.map(tn => t[tn][0]?.TaskID) ?? [])
    }

    useEffect(() => {
        if (amplify)
            CreateAssessmentVM.db = amplify
        
        CreateAssessmentVM.user = user
        _init()
        if (!assessmentFromParams?.ID) {
            setHeader(
                <EMETLDateRangeHeader 
                    submitMessage="Make this a quarterly assessment"
                    onDatesSubmit={handleDateSubmit}
                />
            )
        }

    }, [amplify, u, editingAssessment, user, t])

    const a = persistedAssessment ?? assessment

    return (
        <Container
            id="createAssessment"
            style={{
                padding: "20px",
            }}
        >
            {assessment?.ID && (
                <h1>
                    Assessment {assessment.ID}
                </h1>
            )}
            <Form 
                id="createAssessmentForm"
            >
                <Form.Group as={Row}>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Select Template
                        </Form.Label>
                        <Form.Select
                            onChange={e => {
                                const templateID = e.target.value
                                if (templateID && templates) {
                                    loadTemplateData(templateID)
                                }
                            }}
                        >
                            <option value="">
                                -- Select a Template --
                            </option>
                            {templates ? templates.map(template => (
                                <option 
                                    value={template.ID}
                                    key={template.ID}
                                >
                                    {template.Title}
                                </option>
                            )) : (
                                <option>
                                    No templates available
                                </option>
                            )}
                        </Form.Select>
                    </Form.Group>
                </Form.Group>
                <Form.Group as={Row}>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Assessment Title*
                        </Form.Label>
                        <Form.Control
                            value={persistedAssessment?.Title || assessment?.Title || ""}
                            onChange={
                                e => {
                                    persistedAssessment = { 
                                        ...assessment, 
                                        Title: e.target.value,
                                    }
                                    setAssessment(persistedAssessment)
                                }
                            }
                            placeholder="Enter Assessment Title"
                        />
                    </Form.Group>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Location
                        </Form.Label>
                        <Form.Control
                            value={persistedAssessment?.Location || assessment?.Location || ""}
                            onChange={
                                e => {
                                    persistedAssessment = { 
                                        ...assessment, 
                                        Location: e.target.value,
                                    }
                                    setAssessment(persistedAssessment)
                                }
                            }
                            placeholder="Enter Location"
                        />
                    </Form.Group>
                </Form.Group>
                <Form.Group as={Row}>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Select Unit
                        </Form.Label>
                        <Form.Select
                            onChange={e => {
                                const un = e.target.value
                                if (!un)
                                    return
                                
                                const persistedUnitSelection = parseInt(un)
                                if (!isNaN(persistedUnitSelection) && persistedUnitSelection > 0)
                                    persistedUnit = u[`${persistedUnitSelection - 1}`]
                                else
                                    persistedUnit = null

                                setUnitForAssessment(persistedUnit)
                            }}
                        >
                            <option 
                                value="0"
                            >
                                -- Select a Unit --
                            </option>
                            {u && u.map((unit: EMETL.Unit, index: number) => {
                                const pu = persistedUnit ?? unitForAssessment
                                
                                return (
                                    <option 
                                        value={index + 1}
                                        key={unit.ID}
                                        selected={pu && pu.ID === unit.ID}
                                    >
                                        {unit?.Name}
                                    </option>
                                )
                            })}
                        </Form.Select>
                    </Form.Group>
                </Form.Group>
                <Form.Group as={Row}>
                    <Form.Group as={Col}>
                        <Form.Check
                            type="checkbox"
                            label="Save as Template"
                            name="isTemplate"
                            id="isTemplate"
                            defaultChecked={a?.IsTemplate === 1}
                            onChange={
                                e => {
                                    persistedAssessment = {
                                        ...persistedAssessment,
                                        IsTemplate: e.target.checked ? 1 : 0,
                                    }
                                    setAssessment(persistedAssessment)
                                }
                            }
                        />
                    </Form.Group>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Reference
                        </Form.Label>
                        <Form.Control
                            placeholder="Enter Reference"
                            value={persistedAssessment?.Reference || assessment?.Reference || ""}
                            onChange={
                                e => {
                                    persistedAssessment = { 
                                        ...assessment, 
                                        Reference: e.target.value,
                                    }
                                    setAssessment(persistedAssessment)
                                }
                            }
                        />
                    </Form.Group>
                </Form.Group>
                <Form.Group id="formSCWrapper" className="formSCWrapper">
                    <EMETLSegmentedControl 
                        wrapperClass="trainingTypeWrapper" 
                        options={CreateAssessmentVM.TRAINING_TYPES} 
                        labelName="formSegmentedControl"
                        selectedTab={
                            a?.TrainingType > -1 ? a?.TrainingType : 0
                        }
                        perform={
                            index => {
                                persistedAssessment = {
                                    ...assessment,
                                    TrainingType: index
                                }
                                setAssessment(persistedAssessment)
                            }
                        }
                    />
                </Form.Group>
                <Form.Group 
                    as={Row}
                    style={{
                        borderBottom: "1px solid rgba(110, 110, 110, 0.3)",
                    }}
                >
                    <Form.Group as={Col}>
                        <Form.Label 
                            style={{
                                paddingBottom: "13px",
                            }}
                        >
                            Personnel
                        </Form.Label>
                        <Button
                            id="addAssessorButton"
                            variant="primary"
                            onClick={addOrRemovePersonnel}
                        >
                            Personnel
                        </Button>
                    </Form.Group>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                    <Form.Group as={Col}>
                        {!!dudes?.length && !!selectedPersonnel?.length && selectedPersonnel?.map(sp => {
                            return (
                                <EMETLPersonnel
                                    key={sp}
                                    person={dudes?.find(d => d.ID === sp)}
                                />
                            )
                        })}
                    </Form.Group>
                </Form.Group>
                <Form.Group as={Row}>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Conditions
                        </Form.Label>
                        <Form.Control
                            value={persistedAssessment?.Conditions || assessment?.Conditions || ""}
                            onChange={
                                e => {
                                    persistedAssessment = { 
                                        ...assessment, 
                                        Conditions: e.target.value,
                                    }
                                    setAssessment(persistedAssessment)
                                }
                            }
                            placeholder="Enter Conditions"
                        />
                    </Form.Group>
                </Form.Group>
                <Form.Group as={Row}>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Scenario
                        </Form.Label>
                        <Form.Control
                            value={persistedAssessment?.Scenario || assessment?.Scenario || ""}
                            onChange={
                                e => {
                                    persistedAssessment = { 
                                        ...assessment, 
                                        Scenario: e.target.value,
                                    }
                                    setAssessment(persistedAssessment)
                                }
                            }
                            placeholder="Enter Scenario"
                        />
                    </Form.Group>
                </Form.Group>
                <Form.Group as={Row}>
                    <Form.Group as={Col}>
                        <Form.Label>
                            Results
                        </Form.Label>
                        <Form.Control
                            value={persistedAssessment?.Results || assessment?.Results || ""}
                            onChange={
                                e => {
                                    persistedAssessment = { 
                                        ...assessment, 
                                        Results: e.target.value,
                                    }
                                    setAssessment(persistedAssessment)
                                }
                            }
                            placeholder="Enter Results"
                        />
                    </Form.Group>
                </Form.Group>
                {assessment?.QuarterlyAssessmentDetailID && assessment?.StartDate && assessment?.EndDate && (
                    <Form.Group as={Row} className="my-3">
                        <Form.Group>
                            <h5 className="text-secondary">
                                This is a quarterly assessment from {
                                    new Date(assessment.StartDate).toLocaleDateString()
                                } to {
                                    new Date(assessment.EndDate).toLocaleDateString()
                                } <Button 
                                    onClick={editDates}
                                    variant="outline-secondary"
                                  >
                                    Edit Dates
                                  </Button>
                            </h5>
                        </Form.Group>
                    </Form.Group>
                )}
                <Form.Group 
                    as={Row}
                    className="mt-3"
                    style={{
                        borderBottom: "1px solid rgba(110, 110, 110, 0.3)",
                    }}
                >
                    <Form.Group as={Col}>
                        <Form.Label 
                            style={{
                                paddingBottom: "13px",
                            }}
                        >
                            Tasks
                        </Form.Label>
                        {(!startDate || !endDate) && (
                            <Button
                                id="addTasksButton"
                                variant="primary"
                                onClick={addOrRemoveTasks}
                            >
                                Manage Tasks
                            </Button>
                        )}
                    </Form.Group>
                </Form.Group>
                <Form.Group 
                    as={Row}
                    className="mt-3"
                >   
                    <>
                        {startDate && endDate ? (
                            <h5 className="text-info">
                                This assessment is quarterly (all tasks will be selected).
                            </h5>
                        ) : !!needToAddTasks?.length && (
                                <div
                                    style={{
                                        background: "rgba(50, 255, 100, 0.1)",
                                        paddingLeft: 0,
                                        paddingBottom: "8px",
                                        paddingTop: "8px",
                                    }}
                                >
                                    <h4 
                                        className="text-secondary"
                                        style={{
                                            marginLeft: "8px",
                                            marginTop: "5px",
                                        }}
                                    >
                                        New Tasks to be Saved <Badge bg="success">{needToAddTasks?.length}</Badge>
                                    </h4>
                                    <AssessmentTasks
                                        assessmentID={assessment?.ID}
                                        assessmentTasks={needToAddTasks}
                                        autoSave={async (taskID, rating, needs) => {
                                            if (!assessment?.ID || !taskID)
                                                return false

                                            // Update the data in needToAddTasks
                                            needToAddTasks = needToAddTasks.map(at => {
                                                if (at.TaskID === taskID) {
                                                    return {
                                                        ...at,
                                                        AssessmentID: assessment.ID,
                                                        Rating: rating,
                                                        SpecificNeeds: needs,
                                                    }
                                                }
                                                return at
                                            })


                                            return true
                                        }}
                                        noTasksMessage="There are no additional tasks to be added to the assessment."
                                    />
                                </div>
                            )
                        }
                        {!!assessmentTasks?.length && (
                            <AssessmentTasks
                                assessmentID={assessment?.ID}
                                assessmentTasks={
                                    assessmentTasks.filter(at => selectedTasks.includes(at.TaskID))
                                }
                            />
                        )}
                    </>
                </Form.Group>
            </Form>
            <div
                className="d-grid"
                style={{
                    marginTop: "21px",
                }}
            >
                {assessment?.ID && (
                    <Button 
                        variant="outline-danger" 
                        size="lg" 
                        onClick={deleteAssessment}
                        className="mb-3"
                    >
                        Delete Assessment
                    </Button>
                )}
                <Button 
                    variant="primary" 
                    size="lg" 
                    onClick={() => {
                        createOrUpdateAssessment(
                            needToAddTasks.map(t => {
                                return {
                                    ID: null,
                                    TaskID: t.TaskID,
                                    SpecificNeeds: t.SpecificNeeds,
                                    Rating: t.Rating,
                                }
                            }), 
                            needToRemoveAssessmentTaskIDs,
                            needToAddPersonnel.map(p => {
                                return {
                                    ID: null,
                                    UserID: p,
                                }
                            }),
                            needToRemovePersonnel,
                        )
                    }}
                >
                    {assessmentFromParams || editingAssessment ? "Update Assessment" : "Create Assessment"}
                </Button>
            </div>
        </Container>
    )
}

export default CreateAssessment
